import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Controller } from "react-hook-form";
import LocationFilter from "./LocationFilter";
import { useToastrContext } from "../Provider/ToastrProvider";
import { getCustomerLists, getHotelList } from "../../views/ReservationManagement/ReservationList/api";
import { createQueryParams } from "../../common";
import { customerTableColumn, hotelTableColumn } from "../../views/ReservationManagement/ReservationList/utils";
import RequiredTitle from "../FormGenerator/Fields/RequiredTitle";
import { findCustomerDetails } from "../../views/SiteManagement/Customer/api";
import { findByIdHotel } from "../../views/HotelsManagement/HotelList/api";

const MuiTable = lazy(() => import("../muiTable"));

const DropdownWithDetails = ({ input, control, defaultValue, error, setValue }) => {
  const {
    name,
    label,
    validation,
    defaultValues = defaultValue,
    modalTitle,
    buttonText = "Get List",
    Component,
    isCustomer = false,
    isHotel = false,
  } = input;
  const { sm = 12, md = 12, lg = 12 } = input?.alignment || {};

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState(null);
  const { errorResponseHandler } = useToastrContext();

  const [createModal, setCreateModal] = useState(false);

  const handleSelect = (item, field) => {
    field.onChange(item.id);
    setSelected(item);
    setShowModal(false);
  };

  const handleClear = (field) => {
    field.onChange(null);
    setSelected(null);
  };

  const fetchCustomerList = async (n = undefined, s = undefined, filter = filters) => {
    try {
      const payload = createQueryParams(filter, n, s);
      const response = await getCustomerLists(payload);
      const result = response?.data?.data;
      const data = result?.customers?.map((row) => ({
        ...row,
        cityName: row?.city?.name,
        stateName: row?.state?.name,
      }));
      setRowData(data);
      setTotalCount(result?.totalRecords);
    } catch (error) {
      errorResponseHandler(error);
    }
  };

  const fetchCustomerDetails = async (id) => {
    try {
      if (selected) return;
      let response;

      if (isCustomer) {
        response = await findCustomerDetails(id);
      } else if (isHotel) {
        response = await findByIdHotel(id);
      }

      if (!response) return;
      setSelected(response?.data?.data);
    } catch (error) {
      errorResponseHandler(error);
    }
  };

  const fetchHotelList = async (n = undefined, s = undefined, filter = filters) => {
    try {
      const payload = createQueryParams(filter, n, s);
      const response = await getHotelList(payload);
      const result = response?.data?.data;
      const data = result?.hotels?.map((row) => ({
        ...row,
        city: row?.city?.name,
      }));
      setRowData(data);
      setTotalCount(result?.totalRecords);
    } catch (error) {
      errorResponseHandler(error);
    }
  };

  const handleSearch = (n = undefined, s = undefined, filter) => {
    if (!n && !s) {
      setFilters(filter);
    }

    if (isCustomer) {
      fetchCustomerList(n, s, filter);
    }
    if (isHotel) {
      fetchHotelList(n, s, filter);
    }
  };

  useEffect(() => {
    if (defaultValues) {
      fetchCustomerDetails(defaultValues);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (isCustomer) {
      fetchCustomerList();
    }
    if (isHotel) {
      fetchHotelList();
    }
  }, [isCustomer, isHotel]);

  const handleNavigate = () => {
    setCreateModal(true);
  };

  const tableColumn = (field) => {
    const baseColumn = isCustomer ? [...customerTableColumn] : [...hotelTableColumn];
    const column = [
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 100,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div className="d-flex gap-2">
            <Button
              variant="success"
              size="sm"
              onClick={() => handleSelect(params.row, field)}
            >
              Select
            </Button>
          </div>
        ),
      },
      ...baseColumn,
    ];
    return column;
  };

  return (
    <div
      key={name}
      className={`form-group mb-3 col-sm-${sm} col-md-${md} col-lg-${lg}`}
    >
      <RequiredTitle
        label={label}
        validation={validation}
      />
      <Controller
        name={name}
        control={control}
        rules={validation}
        defaultValue={defaultValues}
        render={({ field }) => (
          <div>
            <div className="d-flex gap-2">
              <input
                id={name}
                name={name}
                type="text"
                disabled
                value={field?.value ? selected?.name : ""}
                className={`form-control ${error ? "is-invalid" : ""}`}
              />
              <button
                type="button"
                onClick={() => handleClear(field)}
                className="btn-sm"
                style={{
                  fontSize: "20px!important",
                  border: "1px solid #C2C2C2",
                  width: "60px",
                  borderRadius: "8px",
                }}
              >
                ×
              </button>
              <Button
                variant="primary"
                className="btn-sm"
                onClick={() => setShowModal(true)}
                style={{ width: "140px" }}
              >
                {buttonText}
              </Button>
              {Component && (
                <Button
                  variant="primary"
                  className="btn-sm"
                  onClick={handleNavigate}
                  style={{ width: "140px" }}
                >
                  Create
                </Button>
              )}
            </div>

            <Modal
              show={createModal}
              onHide={() => setCreateModal(false)}
              size="lg"
            >
              <Modal.Body>
                {Component && React.cloneElement(Component, {
                  isModal: true,
                  onSuccess: (createdItem) => {
                    if (createdItem && createdItem.id) {
                      field.onChange(createdItem.id);
                      setSelected(createdItem);
                    }
                    setCreateModal(false);
                    // refresh list to include the newly created item if table is opened later
                    if (isCustomer) fetchCustomerList();
                  }
                })}
              </Modal.Body>
            </Modal>
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <LocationFilter handleSubmit={(filter) => handleSearch(undefined, undefined, filter)} isCustomer={isCustomer} isHotel={isHotel} />
                <Suspense fallback={<div>Loading...</div>}>
                  <MuiTable
                    rows={rowData}
                    columns={tableColumn(field)}
                    isPaginated={false}
                    rowCount={totalCount}
                    fetchData={handleSearch}
                  />
                </Suspense>
              </Modal.Body>
            </Modal>

            {error && <div className="invalid-feedback">{error}</div>}
          </div>
        )}
      />
    </div>
  );
};

export default DropdownWithDetails;
